// La course (transport, lot T1a) : libellés, trajet, ligne de facture et liens du vol.
// Mêmes règles que le serveur (backend/metier.py : RIDE_KINDS, ORDERER_KINDS, route_of, ride_line_label).

export const RIDE_KINDS = { transfer: "Transfert", hourly: "Mise à disposition" };
export const ORDERER_KINDS = { hotel: "Hôtel", conciergerie: "Conciergerie", agence: "Agence", direct: "Client direct" };
export const PAYERS = { orderer: "Donneur d'ordre", passenger: "Passager" };

// Une course enregistrée avant le lot T1a n'a pas de `ride_kind` : on le déduit de son libellé.
export const rideKindOf = (m) => m?.ride_kind || (m?.type === RIDE_KINDS.hourly ? "hourly" : "transfer");

// « Aéroport de Nice → Hôtel de Paris » (ou la seule adresse connue) ; "" si aucune.
export const routeOf = (m) => [m?.location, m?.dropoff_location].map((x) => (x || "").trim()).filter(Boolean).join(" → ");

const hours = (h) => String(h).replace(".", ",");

// « 20/10/2026 à 10:00 » : prise en charge à l'heure des Réglages (lot T6 ; serveur : metier.ride_when).
export const rideWhen = (m, tz) => {
  const d = new Date(m?.start || "");
  if (Number.isNaN(d.getTime())) return "";
  const fmt = (zone) => new Intl.DateTimeFormat("fr-FR", { timeZone: zone, day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(d).reduce((o, p) => ({ ...o, [p.type]: p.value }), {});
  let p;
  try { p = fmt(tz || "Europe/Monaco"); } catch { p = fmt("Europe/Monaco"); }
  return `${p.day}/${p.month}/${p.year} à ${p.hour}:${p.minute}`;
};

// Taux de TVA d'une course (lot T6 ; serveur : metier.ride_vat_rate) : 0 en franchise ; sinon 10 % transfert, 20 % mise
// à disposition, réglables (Réglages → Prix des courses, lus dans /settings/full ; « à faire confirmer par un comptable »).
export const rideVatRate = (full, m) => {
  if ((full?.vat_regime || "franchise") !== "assujetti") return 0;
  return rideKindOf(m) === "hourly" ? (full?.vat_rate_hourly ?? 0.2) : (full?.vat_rate_transfer ?? 0.1);
};

// Ligne de facture d'une course : « Transfert — 20/10/2026 à 10:00 — Aéroport de Nice → Hôtel de Paris (M. Dupont) ».
// `tz` = fuseau des Réglages (l'heure est celle de la prise en charge à Monaco, pas celle de l'ordinateur).
export const rideLineLabel = (m, tz) => {
  let label = m.type === "Autre" && m.type_detail ? m.type_detail : m.type || "";
  if (m.ride_kind === "hourly") label = RIDE_KINDS.hourly + (m.duration_hours ? ` ${hours(m.duration_hours)} h` : "");
  let texte = [label, rideWhen(m, tz), routeOf(m)].filter(Boolean).join(" — ");
  if ((m.passenger_name || "").trim()) texte += ` (${m.passenger_name.trim()})`;
  if (m.ride_step === "no_show") texte += " — Client absent"; // lot T4a : un no-show est facturé, avec la mention
  return texte;
};

// Espace chauffeur (lot T4a) : étapes fines d'une course. Mêmes règles que le serveur (backend/metier.py :
// STEP_LABELS, DRIVER_NEXT, step_of) ; le statut en est déduit par le serveur.
export const STEP_LABELS = { "": "À faire", enroute: "En route", on_site: "Sur place", on_board: "Client à bord", no_show: "Client absent", done: "Terminé" };
export const STEP_ORDER = ["", "enroute", "on_site", "on_board", "no_show", "done"];
export const DRIVER_NEXT = { "": ["enroute"], enroute: ["on_site", "on_board"], on_site: ["on_board", "no_show"], on_board: ["done"] };
export const UNDO_SECONDS = 120;
export const stepOf = (m) => {
  if (m?.ride_step != null) return m.ride_step;
  return { enroute: "enroute", in_progress: "on_board", completed: "done" }[m?.status] || "";
};
// Pastille de l'étape : mêmes couleurs que les statuts (« Client absent » en rouge).
export const STEP_CLS = {
  "": "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400",
  enroute: "text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400",
  on_site: "text-violet-700 bg-violet-100 dark:bg-violet-900/30 dark:text-violet-300",
  on_board: "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400",
  no_show: "text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300",
  done: "text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400",
};

// Tableau de dispatch (lot T5a) : état d'une course, calculé par le serveur (backend/metier.py : ride_state).
// `bar` = couleur du liseré à gauche de la ligne (posée en style : la couleur des séparations ne l'écrase pas) ;
// « Client absent » : rouge (NO_SHOW_BAR), comme sa pastille STEP_CLS.no_show.
export const DISPATCH_STATES = {
  to_assign: { label: "À attribuer", counter: "À attribuer", tint: "#ea580c", bar: "#f97316",
    cls: "text-orange-700 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-300" },
  not_received: { label: "Pas encore reçue", counter: "Pas encore reçues", tint: "#d97706", bar: "#fbbf24",
    cls: "text-amber-800 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300" },
  received: { label: "Reçue", counter: "Reçues", tint: "#0284c7", bar: "#0ea5e9",
    cls: "text-sky-700 bg-sky-100 dark:bg-sky-900/30 dark:text-sky-300" },
  in_progress: { label: "En cours", counter: "En cours", tint: "#4f46e5", bar: "#6366f1",
    cls: "text-indigo-700 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300" },
  done: { label: "Terminée", counter: "Terminées", tint: "#059669", bar: "#10b981",
    cls: "text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300" },
};
export const NO_SHOW_BAR = "#ef4444";
// Compteurs en tête du tableau, dans cet ordre : leur somme = courses du jour hors annulées.
export const DISPATCH_COUNTERS = ["to_assign", "not_received", "received", "in_progress", "done"];

// Minutes d'attente sur place (arrivée → client à bord ou absent) ; null si l'heure d'arrivée manque.
export const waitMinutes = (m) => {
  const t = m?.step_times || {};
  const fin = t.on_board || t.no_show;
  if (!t.on_site || !fin) return null;
  return Math.max(0, Math.floor((new Date(fin) - new Date(t.on_site)) / 60000));
};

// Adresse vers laquelle guider : l'adresse complète du carnet si le lieu y est relié, sinon le texte saisi.
export const placeAddress = (m, places, which) => {
  const id = which === "pickup" ? m.pickup_place_id : m.dropoff_place_id;
  const p = id && places.find((x) => x.id === id);
  return ((p && p.address) || (which === "pickup" ? m.location : m.dropoff_location) || "").trim();
};
// GPS : gratuit, sans clé ; l'application installée s'ouvre si elle existe, sinon le site.
export const wazeUrl = (adresse) => `https://waze.com/ul?q=${encodeURIComponent(adresse)}&navigate=yes`;
export const mapsUrl = (adresse) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(adresse)}`;
export const telHref = (tel) => `tel:${(tel || "").replace(/[^\d+]/g, "")}`;
// Jour d'une date (AAAA-MM-JJ) dans le fuseau des Réglages.
export const dayIn = (iso, tz) => {
  try { return new Intl.DateTimeFormat("en-CA", { timeZone: tz || "Europe/Monaco" }).format(new Date(iso)); }
  catch { return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Monaco" }).format(new Date(iso)); }
};
export const nextDay = (jour) => new Date(new Date(`${jour}T12:00:00Z`).getTime() + 86400000).toISOString().slice(0, 10);

// Demandes spéciales (lot T1b-2) : « Siège bébé (2 ans) » ; « Animal » sans précision. Même règle que le serveur
// (backend/metier.py : request_text).
export const requestText = (r) => {
  const detail = (r?.detail || "").trim();
  return detail ? `${r.label} (${detail})` : r?.label || "";
};
export const requestsOf = (m) => (Array.isArray(m?.special_requests) ? m.special_requests.filter((r) => r && r.label) : []);

// « Mercedes Classe V (DEMO-001) » ; "" si pas de véhicule.
export const fleetName = (v) => {
  if (!v) return "";
  const nom = [v.brand, v.model].filter(Boolean).join(" ");
  return v.plate ? `${nom} (${v.plate})` : nom;
};

// N° de vol : 2 caractères de compagnie (lettres ou chiffres, dont au moins une lettre) + 1 à 4 chiffres (+ une lettre).
// Un n° de train (ex. « TGV 6171 ») ne donne aucun lien.
export const flightCode = (ref) => {
  const v = (ref || "").toUpperCase().replace(/\s+/g, "");
  return /^(?=[A-Z0-9]{2}\d)(?:[A-Z]{2}|[A-Z]\d|\d[A-Z])\d{1,4}[A-Z]?$/.test(v) ? v : "";
};
// Pages publiques et gratuites (vérifiées le 15/10/2026) : l'historique du vol sur FlightRadar24, et le tableau
// officiel des arrivées de l'aéroport de Nice (pas d'adresse officielle qui ouvre directement un vol donné).
export const flightradarUrl = (code) => `https://www.flightradar24.com/data/flights/${code.toLowerCase()}`;
export const NICE_ARRIVALS_URL = "https://www.nice.aeroport.fr/vols/arrivees";
